import { useState } from 'react';
import { Link } from 'react-router';
import { Button } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { boiteApi, type FiltresBoite } from '../../api/boite-reception.js';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NouvelEmailDialog } from './NouvelEmailDialog.js';
import styles from './Boite.module.css';
interface Props {
  contact?: { id: string; emails: readonly string[]; anonymise?: boolean };
  oeuvreId?: string;
}
export function EchangesBloc(props: Props) {
  const { galerie } = useShellContext();
  if (!galerieAModule(galerie, 'boite-reception') || !galerieALaction(galerie, 'inbox:read'))
    return null;
  return <Echanges {...props} />;
}
function Echanges({ contact, oeuvreId }: Props) {
  const { galerieSlug, galerie } = useShellContext();
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const [ouvert, setOuvert] = useState(false);
  const [page, setPage] = useState(1);
  const filtres: FiltresBoite = { vue: 'toutes', contactId: contact?.id, oeuvreId, page };
  const liste = useQuery({
    queryKey: cleMetier(galerieSlug, 'boite-reception', 'liste', filtres),
    queryFn: (c) => boiteApi.lister(galerieSlug, filtres, appelDeRequete(c)),
  });
  const conversations = liste.data?.conversations ?? [];
  return (
    <section className={`${styles.bloc} ${styles.formulaire}`}>
      <h2>{t('regie:boiteReception.echanges')}</h2>
      {liste.isLoading ? <p role="status">{t('commun:chargement')}</p> : null}
      {liste.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      <ul>
        {conversations.map((c) => (
          <li key={c.id}>
            <Link to={`/${galerieSlug}/boite-reception/${c.id}`}>
              {c.sujet || t('regie:boiteReception.sansSujet')}
            </Link>{' '}
            · {t(`regie:boiteReception.canaux.${c.canal}`)} ·{' '}
            {formatDate(new Date(c.dernierMessageLe), i18n.language === 'en' ? 'en' : 'fr')} ·{' '}
            {t(`regie:boiteReception.etats.${c.etat}`)}
          </li>
        ))}
      </ul>
      {liste.isSuccess && !conversations.length ? <p>{t('regie:boiteReception.vide')}</p> : null}
      <div className={styles.gestes}>
        <Button variant="secondary" disabled={page <= 1} onClick={() => setPage(page - 1)}>
          {t('regie:boiteReception.precedent')}
        </Button>
        <Button
          variant="secondary"
          disabled={(liste.data?.pagination.pages ?? 0) <= page}
          onClick={() => setPage(page + 1)}
        >
          {t('regie:boiteReception.suivant')}
        </Button>
      </div>
      {contact &&
      !contact.anonymise &&
      contact.emails.length > 0 &&
      galerieALaction(galerie, 'inbox:write') ? (
        <Button variant="secondary" onClick={() => setOuvert(true)}>
          {t('regie:boiteReception.ecrireEmail')}
        </Button>
      ) : null}
      {ouvert && contact ? <NouvelEmailDialog contact={contact} onOpenChange={setOuvert} /> : null}
    </section>
  );
}
